import * as React from 'react';

import { observer } from 'mobx-react';

import {
    ALPHA_NUMERIC_INPUT_TYPE,
    DEFAULT_DIGIT_OF_VERIFICATION_CODE,
    DEFAULT_STATUS,
    InputType,
    NUMBER_INPUT_TYPE,
    OtpStatus
} from '~constants/Constants';
import useDebounce from '~utils/hooks/debounce';

export interface IVerificationCodeProps {
    autoFocus?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    length?: number;
    onChange?(data: string): void;
    onCompleted?(data: string): void;
    placeholder?: string;
    type?: InputType;
    status?: OtpStatus;
    value?: string;
    reset?: boolean;
    readonly?: boolean;
}

export const VerificationCode = observer((props: IVerificationCodeProps) => {
    const {
        autoFocus = false,
        inputProps,
        length = DEFAULT_DIGIT_OF_VERIFICATION_CODE,
        onChange,
        onCompleted,
        placeholder = '',
        type = NUMBER_INPUT_TYPE,
        status = DEFAULT_STATUS,
        value: defaultValue = '',
        reset = false,
        readonly
    } = props;

    const confirmCode = onCompleted && useDebounce<string>(onCompleted);

    const fillValues = (value: string) =>
        new Array(length).fill('').map((_, index) => value[index] ?? '');

    const [values, setValues] = React.useState(fillValues(defaultValue));
    const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);

    const inputsRefs = React.useMemo(
        () => new Array(length).fill(null).map(() => React.createRef<HTMLInputElement>()),
        [length]
    );

    const validate = (input: string) => {
        if (type === NUMBER_INPUT_TYPE) {
            return /^\d/.test(input);
        }

        if (type === ALPHA_NUMERIC_INPUT_TYPE) {
            return /^[a-zA-Z0-9]/.test(input);
        }

        return true;
    };

    const selectInputContent = (index: number) => {
        const input = inputsRefs[index].current;

        if (input) {
            requestAnimationFrame(() => {
                input.select();
            });
        }
    };

    const setValue = (value: string, index: number) => {
        const nextValues = [...values];
        nextValues[index] = value;

        setValues(nextValues);

        const stringifiedValues = nextValues.join('');
        const isCompleted = stringifiedValues.length === length;

        if (isCompleted) {
            confirmCode?.(stringifiedValues);

            return;
        }

        onChange?.(stringifiedValues);
    };

    const focusInput = React.useCallback(
        (index: number) => {
            const input = inputsRefs[index]?.current;

            if (input) {
                requestAnimationFrame(() => {
                    input.focus();
                });
            }
        },
        [inputsRefs]
    );

    const blurInput = (index: number) => {
        const input = inputsRefs[index]?.current;

        if (input) {
            requestAnimationFrame(() => {
                input.blur();
            });
        }
    };

    const onInputFocus = (index: number) => {
        const input = inputsRefs[index]?.current;

        if (input) {
            setFocusedIndex(index);
            selectInputContent(index);
        }
    };

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        if (readonly) {
            return;
        }
        const eventValue = event.target.value;
        const value = eventValue.replace(values[index], '');

        if (!validate(value)) {
            selectInputContent(index);

            return;
        }

        if (value.length > 1) {
            setValues(fillValues(eventValue));

            const isCompleted = eventValue.length === length;

            if (isCompleted) {
                confirmCode?.(eventValue);
                blurInput(index);

                return;
            }

            return;
        }

        setValue(value, index);

        if (index === length - 1) {
            blurInput(index);

            return;
        }

        focusInput(index + 1);
    };

    const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (readonly) {
            return;
        }
        const eventKey = event.key;

        if (eventKey === 'Backspace' || eventKey === 'Delete') {
            event.preventDefault();
            setValue('', focusedIndex);
            focusInput(index - 1);

            return;
        }

        if (eventKey === values[index]) {
            focusInput(index + 1);
        }
    };

    const onInputPaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        if (readonly) {
            return;
        }
        event.preventDefault();

        const pastedValue = event.clipboardData.getData('text');
        const nextValues = pastedValue.slice(0, length);

        if (!validate(nextValues)) {
            return;
        }

        setValues(fillValues(nextValues));

        const isCompleted = nextValues.length === length;

        if (isCompleted) {
            confirmCode?.(nextValues);
            blurInput(index);

            return;
        }

        focusInput(nextValues.length);
    };

    React.useEffect(() => {
        if (autoFocus) {
            focusInput(0);
        }
    }, [autoFocus, focusInput, inputsRefs]);

    React.useEffect(() => {
        reset && setValues(fillValues(defaultValue));
    }, [reset]);

    return (
        <div className="elc-iam-verification-code-container">
            {inputsRefs.map((ref, i) => (
                <input
                    type="tel"
                    autoComplete="one-time-code"
                    className={`elc-iam-verification-code-item ${status}`}
                    key={i}
                    onChange={(event) => onInputChange(event, i)}
                    onFocus={() => onInputFocus(i)}
                    onKeyDown={(event) => onInputKeyDown(event, i)}
                    onPaste={(event) => onInputPaste(event, i)}
                    placeholder={placeholder}
                    readOnly={readonly}
                    ref={ref}
                    value={values[i]}
                    {...inputProps}
                />
            ))}
        </div>
    );
});
